import { useEffect, useRef } from "react";

// Cross-tab signal that an email was just verified. The verify-email tab
// (often opened by the email client in a new window) broadcasts on success;
// the original signup tab listens and flips its UI to "verified — sign in".
//
// BroadcastChannel is the primary transport (instant, structured payload).
// A localStorage write is also performed as a fallback for browsers/contexts
// that don't expose BroadcastChannel — `storage` events fire on every other
// tab in the same origin.
const CHANNEL_NAME = "oneshift-email-verified";
const STORAGE_KEY = "oneshift:email-verified";

export interface EmailVerifiedEvent {
  email: string;
  at: number;
}

export function broadcastEmailVerified(email: string): void {
  const event: EmailVerifiedEvent = { email, at: Date.now() };
  if (typeof BroadcastChannel !== "undefined") {
    const channel = new BroadcastChannel(CHANNEL_NAME);
    channel.postMessage(event);
    channel.close();
  }
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(event));
  } catch {
    // localStorage may be unavailable in private modes; broadcast still wins.
  }
}

export function useEmailVerifiedListener(
  handler: (event: EmailVerifiedEvent) => void,
): void {
  // Keep the latest handler in a ref so subscribing once is enough — re-running
  // the effect on every render would tear down/rebuild the channel needlessly.
  const handlerRef = useRef(handler);
  useEffect(() => {
    handlerRef.current = handler;
  });

  useEffect(() => {
    const dispatch = (event: EmailVerifiedEvent) => handlerRef.current(event);

    let channel: BroadcastChannel | null = null;
    if (typeof BroadcastChannel !== "undefined") {
      channel = new BroadcastChannel(CHANNEL_NAME);
      channel.onmessage = (ev: MessageEvent<EmailVerifiedEvent>) => dispatch(ev.data);
    }

    const onStorage = (ev: StorageEvent) => {
      if (ev.key !== STORAGE_KEY || !ev.newValue) return;
      try {
        dispatch(JSON.parse(ev.newValue) as EmailVerifiedEvent);
      } catch {
        // ignore malformed payloads
      }
    };
    window.addEventListener("storage", onStorage);

    return () => {
      channel?.close();
      window.removeEventListener("storage", onStorage);
    };
  }, []);
}
